/**
 * Strike Movers client — the strikes whose RTL order-flow changed most inside a time window
 * (GET /api/rtl-flow/movers). Typed, with a short cache so the tape's 7 s poll and a filter
 * change inside the same 10 s share one request.
 */
import { getApiUrl } from '../config/api';

export type MoverSide = 'C' | 'P';
export type MoverDirection = 'bought' | 'sold';
export type MoverRead = 'call_sold' | 'call_bought' | 'put_sold' | 'put_bought';
export type PatternKind = 'premium_sold_both_sides' | 'premium_bought_both_sides' | 'cluster_imbalance';
export type PatternNoteKey = 'range_or_up_lean' | 'breakout_lean' | 'cluster_sold' | 'cluster_bought';
/** Which population the z-score is measured against. */
export type ZMode = 'all' | 'trim95' | 'mad' | 'cluster';
/** Which baseline the "ratio ×" reading is taken over. */
export type RatioMode = 'avg_all' | 'avg95' | 'p95' | 'median' | 'own' | 'vol';
export type SortMode = 'magnitude' | 'z' | 'spike' | 'ratio' | 'share';
export type ClusterSideBias = 'calls' | 'puts' | 'mixed';

/** The detection measures the backend computes per mover; any may be null when undefined. */
export interface MoverMeasures {
  /** z against all strikes / the 95% cluster / robust (median + MAD) / the mover's own cluster. */
  z_all: number | null;
  z_trim95: number | null;
  z_mad: number | null;
  z_cluster: number | null;
  /** |change| over the average |change| of all strikes / of the 95% cluster / over the 95th percentile / over the median. */
  ratio_avg_all: number | null;
  ratio_avg95: number | null;
  ratio_p95: number | null;
  ratio_median: number | null;
  /** |change| over this strike's own normal change. */
  rel_own: number | null;
  /** This strike's share of the total |change| (0..1). */
  share_total: number | null;
  /** (buy − sell) / (buy + sell) over the window, −1..1. */
  imbalance: number | null;
  /** Window volume over the average strike's window volume. */
  vol_ratio_all: number | null;
}
export const MEASURE_KEYS: (keyof MoverMeasures)[] = ['z_all', 'z_trim95', 'z_mad', 'z_cluster', 'ratio_avg_all', 'ratio_avg95', 'ratio_p95', 'ratio_median', 'rel_own', 'share_total', 'imbalance', 'vol_ratio_all'];
export const Z_MEASURE: Record<ZMode, keyof MoverMeasures> = { all: 'z_all', trim95: 'z_trim95', mad: 'z_mad', cluster: 'z_cluster' };
export const RATIO_MEASURE: Record<RatioMode, keyof MoverMeasures> = { avg_all: 'ratio_avg_all', avg95: 'ratio_avg95', p95: 'ratio_p95', median: 'ratio_median', own: 'rel_own', vol: 'vol_ratio_all' };

/** A run of adjacent strikes that moved together (≤12 per response, by volume desc). */
export interface MoverCluster {
  id: string;
  strike_lo: number;
  strike_hi: number;
  n_strikes: number;
  call_net: number | null;
  put_net: number | null;
  change_net: number | null;
  volume: number | null;
  /** Share of the window's volume / of the total |change| (0..1). */
  share_volume: number | null;
  share_abs_change: number | null;
  /** (buy − sell) / (buy + sell), −1..1. */
  imbalance: number | null;
  /** (calls − puts) / (calls + puts) by |net|, −1..1. */
  call_put_imbalance: number | null;
  z_vs_clusters: number | null;
  side_bias: ClusterSideBias;
  above_ref: boolean | null;
}

export interface Mover {
  strike: number;
  side: MoverSide;
  /** Net change over the window (signed; sold = negative). */
  change_net: number | null;
  change_buy: number | null;
  change_sell: number | null;
  /** |change| used for ranking. */
  magnitude: number | null;
  direction: MoverDirection;
  read: MoverRead;
  /** Deviation of this strike's change from the mean change of all strikes, in std units. */
  z: number | null;
  /** Peak-interval volume over the strike's normal interval volume. */
  spike_ratio: number | null;
  /** Epoch ms of the busiest interval. */
  peak_t: number | null;
  peak_vol: number | null;
  avg_interval_vol: number | null;
  start_net: number | null;
  end_net: number | null;
  first_t: number | null;
  last_t: number | null;
  cluster_id: string | null;
  measures: MoverMeasures;
}

export interface PairPattern {
  kind: 'premium_sold_both_sides' | 'premium_bought_both_sides';
  call_strike: number;
  put_strike: number;
  call_sold?: number | null;
  put_sold?: number | null;
  call_bought?: number | null;
  put_bought?: number | null;
  note_key: 'range_or_up_lean' | 'breakout_lean';
}
export interface ClusterPattern {
  kind: 'cluster_imbalance';
  cluster_id: string;
  strike_lo: number;
  strike_hi: number;
  imbalance: number | null;
  side_bias: ClusterSideBias;
  note_key: 'cluster_sold' | 'cluster_bought';
}
export type MoverPattern = PairPattern | ClusterPattern;

export interface MoversImbalance {
  calls_net: number | null;
  puts_net: number | null;
  above_ref_net: number | null;
  below_ref_net: number | null;
  /** (calls − puts) / (|calls| + |puts|) and the same above/below the reference, −1..1. */
  calls_vs_puts: number | null;
  above_vs_below: number | null;
}

export interface MoversStats {
  n_pairs: number;
  mean_change: number | null;
  std_change: number | null;
  intervals: number;
  imbalance: MoversImbalance | null;
  /** The modes the backend answered with (zmode / ratio / sort echoed back). */
  modes: Record<string, string>;
}

export interface MoversWindow {
  /** Epoch ms (the backend may also send ISO strings; both are normalised). */
  from: number | null;
  to: number | null;
  minutes: number;
}

export interface MoversResponse {
  success: boolean;
  status?: number;
  error?: string;
  underlying: string;
  expiry: string;
  session: string;
  window: MoversWindow;
  stats: MoversStats;
  movers: Mover[];
  clusters: MoverCluster[];
  patterns: MoverPattern[];
  timeseriesdb_available: boolean;
}

export interface MoversQuery {
  underlying: string;
  expiry?: string;
  session?: string;
  /** "HH:MM" New York time, inclusive window bounds inside the session's RTH. */
  from?: string;
  to?: string;
  windowMin: number;
  minChange?: number;
  minZ?: number;
  minSpike?: number;
  minRatio?: number;
  zmode?: ZMode;
  ratio?: RatioMode;
  sort?: SortMode;
  sides: MoverSide[];
  top?: number;
}

const CACHE_MS = 10_000;
const cache = new Map<string, { at: number; data: MoversResponse }>();
const inflight = new Map<string, Promise<MoversResponse>>();

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const ms = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? (v < 1e11 ? v * 1000 : v) : null; // seconds vs ms
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
};
const side = (v: unknown): MoverSide => (String(v || '').toUpperCase().startsWith('P') ? 'P' : 'C');
const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));
const bool = (v: unknown): boolean | null => (v === null || v === undefined ? null : v === true || v === 1 || v === 'true');
const clamp1 = (v: number | null): number | null => (v === null ? null : Math.max(-1, Math.min(1, v)));
const sideBias = (v: unknown): ClusterSideBias => (v === 'calls' || v === 'puts' ? v : 'mixed');

function normaliseMeasures(raw: any): MoverMeasures {
  const m = raw && typeof raw === 'object' ? raw : {};
  const out = {} as MoverMeasures;
  for (const k of MEASURE_KEYS) out[k] = num(m[k]);
  out.imbalance = clamp1(out.imbalance);
  return out;
}

function normaliseMover(raw: any): Mover | null {
  const strike = num(raw?.strike);
  if (strike === null) return null;
  const s = side(raw?.side);
  const changeNet = num(raw?.change_net);
  const direction: MoverDirection =
    raw?.direction === 'sold' || raw?.direction === 'bought' ? raw.direction : (changeNet ?? 0) < 0 ? 'sold' : 'bought';
  const read: MoverRead = (raw?.read as MoverRead) || (`${s === 'C' ? 'call' : 'put'}_${direction}` as MoverRead);
  return {
    strike,
    side: s,
    change_net: changeNet,
    change_buy: num(raw?.change_buy),
    change_sell: num(raw?.change_sell),
    magnitude: num(raw?.magnitude) ?? (changeNet === null ? null : Math.abs(changeNet)),
    direction,
    read,
    z: num(raw?.z),
    spike_ratio: num(raw?.spike_ratio),
    peak_t: ms(raw?.peak_t),
    peak_vol: num(raw?.peak_vol),
    avg_interval_vol: num(raw?.avg_interval_vol),
    start_net: num(raw?.start_net),
    end_net: num(raw?.end_net),
    first_t: ms(raw?.first_t),
    last_t: ms(raw?.last_t),
    cluster_id: str(raw?.cluster_id),
    measures: normaliseMeasures(raw?.measures),
  };
}

function normaliseCluster(raw: any): MoverCluster | null {
  const lo = num(raw?.strike_lo), hi = num(raw?.strike_hi);
  if (lo === null || hi === null) return null;
  return {
    id: str(raw?.id) ?? `${lo}-${hi}`,
    strike_lo: Math.min(lo, hi), strike_hi: Math.max(lo, hi),
    n_strikes: num(raw?.n_strikes) ?? 0,
    call_net: num(raw?.call_net), put_net: num(raw?.put_net), change_net: num(raw?.change_net),
    volume: num(raw?.volume), share_volume: num(raw?.share_volume), share_abs_change: num(raw?.share_abs_change),
    imbalance: clamp1(num(raw?.imbalance)), call_put_imbalance: clamp1(num(raw?.call_put_imbalance)),
    z_vs_clusters: num(raw?.z_vs_clusters),
    side_bias: sideBias(raw?.side_bias),
    above_ref: bool(raw?.above_ref),
  };
}

function normalisePattern(raw: any): MoverPattern | null {
  if (raw?.kind === 'cluster_imbalance') {
    const lo = num(raw?.strike_lo), hi = num(raw?.strike_hi);
    if (lo === null || hi === null) return null;
    const imbalance = clamp1(num(raw?.imbalance));
    const note_key: ClusterPattern['note_key'] = raw?.note_key === 'cluster_bought' ? 'cluster_bought' : raw?.note_key === 'cluster_sold' ? 'cluster_sold' : (imbalance ?? 0) > 0 ? 'cluster_bought' : 'cluster_sold';
    return { kind: 'cluster_imbalance', cluster_id: str(raw?.cluster_id) ?? `${lo}-${hi}`, strike_lo: Math.min(lo, hi), strike_hi: Math.max(lo, hi), imbalance, side_bias: sideBias(raw?.side_bias), note_key };
  }
  const cs = num(raw?.call_strike), ps = num(raw?.put_strike);
  if (cs === null || ps === null) return null;
  const kind: PairPattern['kind'] = raw?.kind === 'premium_bought_both_sides' ? 'premium_bought_both_sides' : 'premium_sold_both_sides';
  const note_key: PairPattern['note_key'] = raw?.note_key === 'breakout_lean' ? 'breakout_lean' : raw?.note_key === 'range_or_up_lean' ? 'range_or_up_lean' : kind === 'premium_bought_both_sides' ? 'breakout_lean' : 'range_or_up_lean';
  return { kind, call_strike: cs, put_strike: ps, call_sold: num(raw?.call_sold), put_sold: num(raw?.put_sold), call_bought: num(raw?.call_bought), put_bought: num(raw?.put_bought), note_key };
}

function normaliseImbalance(raw: any): MoversImbalance | null {
  if (!raw || typeof raw !== 'object') return null;
  return {
    calls_net: num(raw.calls_net), puts_net: num(raw.puts_net), above_ref_net: num(raw.above_ref_net), below_ref_net: num(raw.below_ref_net),
    calls_vs_puts: clamp1(num(raw.calls_vs_puts)), above_vs_below: clamp1(num(raw.above_vs_below)),
  };
}

export function normaliseMoversResponse(raw: any, q: MoversQuery): MoversResponse {
  const movers = Array.isArray(raw?.movers) ? (raw.movers.map(normaliseMover).filter(Boolean) as Mover[]) : [];
  const clusters = Array.isArray(raw?.clusters) ? (raw.clusters.map(normaliseCluster).filter(Boolean) as MoverCluster[]).slice(0, 12) : [];
  const patterns = Array.isArray(raw?.patterns) ? (raw.patterns.map(normalisePattern).filter(Boolean) as MoverPattern[]) : [];
  const w = raw?.window || {};
  const st = raw?.stats || {};
  return {
    success: raw?.success === true,
    status: raw?.status,
    error: raw?.error,
    underlying: String(raw?.underlying || q.underlying),
    expiry: String(raw?.expiry || q.expiry || ''),
    session: String(raw?.session || q.session || ''),
    window: { from: ms(w.from), to: ms(w.to), minutes: num(w.minutes) ?? q.windowMin },
    stats: {
      n_pairs: num(st.n_pairs) ?? movers.length, mean_change: num(st.mean_change), std_change: num(st.std_change), intervals: num(st.intervals) ?? 0,
      imbalance: normaliseImbalance(st.imbalance),
      modes: st.modes && typeof st.modes === 'object' ? Object.fromEntries(Object.entries(st.modes).map(([k, v]) => [k, String(v)])) : {},
    },
    movers,
    clusters,
    patterns,
    timeseriesdb_available: raw?.timeseriesdb_available !== false,
  };
}

export function moversUrl(q: MoversQuery): string {
  const p = new URLSearchParams();
  p.set('underlying', q.underlying);
  if (q.expiry) p.set('expiry', q.expiry);
  if (q.session) p.set('session', q.session);
  p.set('days', '1');
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  p.set('window_min', String(Math.max(5, Math.round(q.windowMin))));
  p.set('min_change', String(q.minChange ?? 0));
  p.set('min_z', String(q.minZ ?? 0));
  p.set('min_spike', String(q.minSpike ?? 0));
  p.set('min_ratio', String(q.minRatio ?? 0));
  if (q.zmode) p.set('zmode', q.zmode);
  if (q.ratio) p.set('ratio', q.ratio);
  if (q.sort) p.set('sort', q.sort);
  p.set('sides', (q.sides.length ? q.sides : ['C', 'P']).join(','));
  p.set('top', String(q.top ?? 25));
  return `/api/rtl-flow/movers?${p.toString()}`;
}

/** Fetch the movers for a window; identical queries inside 10 s share one response. */
export async function fetchFlowMovers(q: MoversQuery, opts: { force?: boolean } = {}): Promise<MoversResponse> {
  const url = moversUrl(q);
  const now = Date.now();
  const hit = cache.get(url);
  if (!opts.force && hit && now - hit.at < CACHE_MS) return hit.data;
  const pending = inflight.get(url);
  if (pending) return pending;
  const p = (async () => {
    try {
      const r = await fetch(getApiUrl(url), { credentials: 'include' });
      const body = r.ok ? await r.json().catch(() => null) : null;
      const data = normaliseMoversResponse(body ?? { success: false, status: r.status }, q);
      if (!r.ok) { data.success = false; data.status = r.status; }
      if (data.success) cache.set(url, { at: Date.now(), data });
      return data;
    } catch {
      return normaliseMoversResponse({ success: false }, q);
    } finally {
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

export function clearFlowMoversCache(): void { cache.clear(); }
